import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, IconButton, Typography } from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useRouteMatch } from "react-router-dom";
import { useFormikContext } from "formik";
import {
  getValidLanguagesFromUser,
  isNewsLanguageCompleteForSave,
  LANGUAGE_LABELS,
} from "../../shared/languageConfig";
import { LanguageChips } from "../../shared/LanguageSelector";

const Publish = ({ previewLink, title, buttonText }) => {
  const LOCALE_BY_LANGUAGE = {
    en: "en-GB",
    it: "it-IT",
    fr: "fr-FR",
    es: "es-ES",
  };

  const match = useRouteMatch();
  const { id } = match.params;
  const formik = useFormikContext();

  const completedLanguages = useMemo(() => {
    const validLanguages = getValidLanguagesFromUser({
      validLanguages: formik.values.validLanguages,
    });

    return validLanguages.filter((language) =>
      isNewsLanguageCompleteForSave(formik.values, language)
    );
  }, [formik.values]);

  const [selectedLanguage, setSelectedLanguage] = useState(null);

  useEffect(() => {
    if (!completedLanguages.length) {
      setSelectedLanguage(null);
      return;
    }

    setSelectedLanguage((current) =>
      current && completedLanguages.includes(current)
        ? current
        : completedLanguages[0]
    );
  }, [completedLanguages]);

  const languageCompletionByCode = useMemo(
    () =>
      completedLanguages.reduce((acc, language) => {
        acc[language] = language === selectedLanguage ? "active" : "complete";
        return acc;
      }, {}),
    [completedLanguages, selectedLanguage]
  );

  const handlePreview = useCallback(
    (language) => {
      if (!id || id === "new") return;
      const locale = LOCALE_BY_LANGUAGE[language] || "en-GB";
      window.open(
        `${previewLink}?news_id=${id}&isPreview=true&locale=${locale}`,
        "_blank",
        "noreferrer"
      );
    },
    [previewLink, id]
  );

  return (
    <Grid item xs={12}>
      <Grid item>
        <Typography variant="h6">{title}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid item>
          <Typography color="textSecondary" variant="h6">
            {buttonText}
          </Typography>
        </Grid>
        {completedLanguages.length > 0 && (
          <Box mt={1}>
            <Typography color="textSecondary" variant="body2">
              Select a language to preview
            </Typography>
            <Box mt={1}>
              <LanguageChips
                languageCompletionByCode={languageCompletionByCode}
                languages={completedLanguages}
                onLanguageClick={setSelectedLanguage}
              />
            </Box>
          </Box>
        )}
        {selectedLanguage && (
          <Grid item>
            <Typography color="textSecondary" variant="h6">
              Preview in{" "}
              {LANGUAGE_LABELS[selectedLanguage] ||
                selectedLanguage.toUpperCase()}
              <IconButton
                disabled={!id || id === "new"}
                onClick={() => handlePreview(selectedLanguage)}
              >
                <VisibilityIcon />
              </IconButton>
            </Typography>
          </Grid>
        )}
        {completedLanguages.length === 0 && (
          <Box mt={1}>
            <Typography color="textSecondary" variant="body2">
              No complete language is ready for preview yet
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default Publish;
